import { isEmpty } from "lodash";
import { useState } from "react";
import { createRecipientGroup } from "src/api/contractSweepRecipientSetting";
import DialogInOne from "src/components/DialogInOne";
import { useAssets, useChains } from "src/components/FetchConfig";
import { Button } from "src/components/MuiGenerals";
import YubiPrompter from "src/components/Prompter";
import { usePageConfigProviderContext } from "src/features/PageConfigProvider";
import {
  useAlerting,
  usePermission,
  useTranslation,
  useYubiPrompt,
} from "src/hooks";
import AddGroupDialogContent from "../components/AddGroupDialogContent";
import { initAddGroupFields } from "../types/AddGroupFields";
import { AddGroupFields } from "../types/AddGroupFields";
import { useZusDialogStore } from "src/zustand/store";
import { FeatureCodes } from "src/utils/constant";
import { toDBInDecimals } from "src/utils/helper";
import { validateRotateAmountMaxAmountLimit } from "../helpers/validateRotateAmountMaxAmountLimit";
import { appError } from "src/utils/errorHandler";

const AddPermission =
  FeatureCodes.assetManagement.ContractSweepRecipentSetting
    .ContractSweepRecipentGroup.addRecord;

const useAddRecordDialog = () => {
  const { translatePrefix, onFilterSearch } = usePageConfigProviderContext();
  const { getResAfterYubi, prompterConfig } = useYubiPrompt();
  const { hasPermission } = usePermission();
  const { t } = useTranslation(translatePrefix);
  const { selectChainByPieces } = useChains();
  const { alerting } = useAlerting();
  const zusDialog = useZusDialogStore();
  const [isOpen, setIsOpen] = useState(false);
  const [fields, setFields] = useState<AddGroupFields>(initAddGroupFields);
  const { selectAssetsByChain } = useAssets();

  const { chain_id, chain_type } = selectChainByPieces({
    chain_name: fields.chainName,
  });
  const assets = selectAssetsByChain({ chain_type, chain_id });

  const validateForm = () => {
    return validateRotateAmountMaxAmountLimit(
      fields.rotateAmountLimit,
      fields.maximumAmountLimit,
      fields.unlimitAmount,
    );
  };

  const onConfirm = async () => {
    const selectedAsset = assets.find(
      (item) => item.asset_name === fields.assetName,
    );

    const maxAmountLimit = fields.unlimitAmount
      ? 0
      : toDBInDecimals(fields.maximumAmountLimit, selectedAsset?.decimals ?? 0);

    const rotateAmountLimit = fields.unlimitAmount
      ? 0
      : toDBInDecimals(fields.rotateAmountLimit, selectedAsset?.decimals ?? 0);

    const validateResult = validateForm();

    if (!validateResult.isValid) {
      alerting("error", t(validateResult.errorMsg));
      return;
    }

    zusDialog.openExtra("loadingDialog");
    const res = await getResAfterYubi(
      createRecipientGroup,
      {
        groupSeq: fields.groupSequence,
        groupType: fields.groupType,
        chainType: chain_type,
        chainId: chain_id,
        assetName: fields.assetName,
        maxAmountLimit,
        rotateAmountLimit,
        decimals: selectedAsset?.decimals,
        status: 1,
      },
      {
        isSkipErrorHandling: true,
      },
    );
    zusDialog.closeExtra();

    if (!res) return;

    if ((res as any).error?.code === -51) {
      return alerting("warning", t("duplicate_group_sequence"));
    }

    if (!res || (res as any).error) {
      return appError({
        ...(res as any).error,
        requestId: (res as any).requestId,
      });
    }

    alerting("success", t("add_record_success"));
    onFilterSearch();
    onClose();
  };

  const onClose = () => {
    setIsOpen(false);
    setFields(initAddGroupFields);
  };

  const isConfirmDisabled =
    isEmpty(fields.chainName) ||
    isEmpty(fields.assetName) ||
    isEmpty(fields.groupSequence) ||
    isEmpty(fields.groupType) ||
    (!fields.unlimitAmount && isEmpty(fields.maximumAmountLimit));

  const renderAddGroupButtonAndDialog = () => {
    return (
      <>
        <YubiPrompter {...prompterConfig} />
        {hasPermission(AddPermission) && (
          <Button onClick={() => setIsOpen(true)} variant="outlined">
            {t("add_record")}
          </Button>
        )}
        <DialogInOne
          title={t("add_record")}
          self={{
            open: isOpen,
            onClose: onClose,
          }}
          content={
            <AddGroupDialogContent fields={fields} setFields={setFields} />
          }
          isConfirmDisabled={isConfirmDisabled}
          onConfirm={onConfirm}
          onCancel={onClose}
        />
      </>
    );
  };

  return { renderAddGroupButtonAndDialog };
};

export default useAddRecordDialog;
